import { useEffect, useRef, useState } from 'react'
import { useLenis } from 'lenis/react'
import { call, type CallRequest } from '@/content/site'
import { useInView } from '@/hooks/useInView'
import { cx } from '@/lib/cx'
import { clamp, damp } from '@/lib/math'
import styles from './Marquee.module.css'
import { onTick } from './motion'

/** Base drift in px/s: slow enough to read a phrase as it passes. */
const BASE_SPEED = 34

function Request({ request }: { request: CallRequest }) {
  return (
    <li className={styles.item} lang={request.langCode}>
      <span className={styles.phrase}>{request.text}</span>
      <span className={styles.meta} lang="en">
        <span className={styles.language}>{request.lang}</span>
        {request.en ? <span className={styles.gloss}>{request.en}</span> : null}
      </span>
    </li>
  )
}

/** Props for {@link Marquee}. */
export interface MarqueeProps {
  /** Reduced motion: a static, wrapped list instead of a moving band (and no toggle). */
  reduced: boolean
}

/**
 * A slow, endless band of the things owners ask, in their own languages. It speeds up
 * slightly with scroll velocity (Lenis), eases to a stop under the pointer, and pauses
 * offscreen. A glass toggle (`aria-pressed`) stops it completely, for WCAG 2.2.2. Each
 * request carries its `lang`; the duplicate copy is `aria-hidden`.
 */
export function Marquee({ reduced }: MarqueeProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const railRef = useRef<HTMLDivElement>(null)
  const groupRef = useRef<HTMLUListElement>(null)
  const velocity = useRef(0)
  const hovered = useRef(false)
  const pausedRef = useRef(false)
  const offset = useRef(0)
  const speed = useRef(BASE_SPEED)
  const [paused, setPaused] = useState(false)
  const inView = useInView(rootRef, { rootMargin: '10% 0px' })

  useLenis(({ velocity: v }) => {
    velocity.current = v
  }, [])

  useEffect(() => {
    const rail = railRef.current
    const group = groupRef.current
    if (reduced || !inView || !rail || !group) return

    let period = group.offsetWidth
    const observer = new ResizeObserver(() => {
      period = group.offsetWidth
    })
    observer.observe(group)

    const remove = onTick((dt) => {
      // Lenis velocity is px per frame; it decays here too, in case scrolling stops abruptly.
      velocity.current = damp(velocity.current, 0, 3, dt)
      const stopped = pausedRef.current || hovered.current
      const boost = clamp(Math.abs(velocity.current) * 9, 0, 240)
      speed.current = damp(speed.current, stopped ? 0 : BASE_SPEED + boost, stopped ? 2.6 : 3, dt)
      // Paused means still: once the ease-out has nearly settled, stop writing entirely.
      if (stopped && speed.current < 0.05) {
        speed.current = 0
        return
      }
      if (period <= 0) return
      offset.current = (offset.current + speed.current * dt) % period
      rail.style.transform = `translate3d(${(-offset.current).toFixed(2)}px, 0, 0)`
    })

    return () => {
      remove()
      observer.disconnect()
    }
  }, [reduced, inView])

  const toggle = () => {
    pausedRef.current = !pausedRef.current
    setPaused(pausedRef.current)
  }

  return (
    <div className={styles.shell}>
      <div
        ref={rootRef}
        className={cx(styles.root, reduced && styles.still)}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') hovered.current = true
        }}
        onPointerLeave={() => {
          hovered.current = false
        }}
      >
        <div ref={railRef} className={styles.rail}>
          <ul ref={groupRef} className={styles.group}>
            {call.requests.map((request) => (
              <Request key={request.text} request={request} />
            ))}
          </ul>
          {reduced ? null : (
            <ul className={styles.group} aria-hidden="true">
              {call.requests.map((request) => (
                <Request key={request.text} request={request} />
              ))}
            </ul>
          )}
        </div>
      </div>

      {reduced ? null : (
        // A constant name with aria-pressed (APG toggle pattern); the tooltip names the next action.
        <button
          type="button"
          className={styles.toggle}
          aria-pressed={paused}
          aria-label={call.marqueeControls.pause}
          title={paused ? call.marqueeControls.play : call.marqueeControls.pause}
          onClick={toggle}
          data-cursor="link"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            {paused ? <path d="M9 7.2v9.6l7.6-4.8z" /> : <path d="M9.25 7.5v9M14.75 7.5v9" />}
          </svg>
        </button>
      )}
    </div>
  )
}
